'use client';

import { useState, useRef, useEffect } from 'react';
import { songs } from './songs';
import { Button } from '@/components/ui/button';

import { track } from '@vercel/analytics';

import posthog from 'posthog-js';

export default function KillersGame() {
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [currentDuration, setCurrentDuration] = useState(1); // Start with 1 second
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [guesses, setGuesses] = useState<string[]>([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(4); // 1s, 2s, 4s, 8s
  const [gameStarted, setGameStarted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const currentSong = songs[currentSongIndex];

  useEffect(() => {
    if (gameOver) {
      // Wait 5 seconds, then show the modal
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 1000);

      // Cleanup in case the component unmounts or gameOver changes
      return () => clearTimeout(timer);
    }
  }, [gameOver]);

  const playClip = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();

      setTimeout(() => {
        audioRef.current?.pause();
      }, currentDuration * 1000);
    }
  };

  const handleGuess = () => {
    const isCorrect =
      currentGuess.toLowerCase().trim() === currentSong.title.toLowerCase();

    if (isCorrect) {
      // Score based on how many attempts it took
      const points =
        attemptsLeft === 4
          ? 8
          : attemptsLeft === 3
            ? 4
            : attemptsLeft === 2
              ? 2
              : 1;
      setScore(score + points);
      setShowAnswer(true);

      setTimeout(() => {
        nextSong();
      }, 2000);
    } else {
      setGuesses([...guesses, currentGuess]);
      setAttemptsLeft(attemptsLeft - 1);
      setCurrentGuess('');

      if (attemptsLeft <= 1) {
        // Out of attempts
        setShowAnswer(true);
        setTimeout(() => {
          nextSong();
        }, 3000);
      } else {
        // Double the duration for next attempt
        setCurrentDuration(currentDuration * 2);
      }
    }
  };

  const nextSong = () => {
    if (currentSongIndex >= songs.length - 1) {
      setGameOver(true);
    } else {
      setCurrentSongIndex(currentSongIndex + 1);
      setCurrentDuration(1);
      setAttemptsLeft(4);
      setShowAnswer(false);
      setGuesses([]);
      setCurrentGuess('');
    }
  };

  const restartGame = () => {
    setCurrentSongIndex(0);
    setScore(0);
    setGameOver(false);
    setCurrentDuration(1);
    setAttemptsLeft(4);
    setShowAnswer(false);
    setGuesses([]);
    setCurrentGuess('');
    setGameStarted(false);
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-[720px] bg-white bg-opacity-90 p-8 md:p-12 rounded-lg border-4 border-gray-900">
          <h1 className="text-5xl md:text-7xl font-[900] mb-6 text-gray-900">
            THE KILLERS QUIZ
          </h1>
          <p className="text-xl md:text-2xl text-gray-900 mb-8">
            Can you guess The Killers songs from just a few seconds of audio?
          </p>

          <div className="bg-gray-100 p-6 rounded-md mb-8 text-left">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">
              How to Play:
            </h2>
            <ul className="space-y-3 text-lg text-gray-900">
              <li>🎵 Listen to short clips of The Killers songs</li>
              <li>⏱️ Start with 1 second, then 2s, 4s, and 8s</li>
              <li>🎯 Guess correctly on the first try for 8 points!</li>
              <li>📉 Points decrease with each attempt (8 → 4 → 2 → 1)</li>
              <li>🏆 Get the highest score possible!</li>
            </ul>
          </div>

          <p className="text-lg text-gray-700 mb-8">
            Total Songs: <span className="font-bold">{songs.length}</span>
            <br />
            Max Score: <span className="font-bold">{songs.length * 8}</span>
          </p>

          <button
            onClick={() => {
              track('killers_quiz_started'); // Keep Vercel Analytics
              posthog.capture('quiz_started'); // Add PostHog tracking
              setGameStarted(true);
            }}
            className="bg-[#128D7E] hover:bg-[#085950] text-white px-12 py-5 rounded-md text-2xl font-bold transition"
          >
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className=" max-w-[640px]">
          <h1 className="text-2xl md:text-2xl mb-2 text-gray-900 font-[900]">
            YOUR SCORE:
          </h1>
          <h1 className="text-8xl  font-[900] mb-4 text-gray-900">
            {score}/{songs.length * 8}
          </h1>
          <p className="text-2xl md:text-3xl mb-8 text-gray-900">
            Awesome work, most people can't get over 25% on this!
          </p>
          <div className="flex gap-4">
            <a href="/the-killers-hard">
              <button className="bg-[#07534a] hover:bg-[#085950] text-white px-8 py-4 rounded-md text-xl font-bold transition">
                Try the harder quiz!
              </button>
            </a>
            <button
              onClick={restartGame}
              className="bg-[#128D7E] hover:bg-[#085950] text-white px-8 py-4 rounded-md text-xl font-bold transition"
            >
              Play Again
            </button>
          </div>

          {/* Slide-up Modal */}
          {showModal && (
            <div
              className="fixed inset-0 z-50 flex items-end justify-center bg-black/5 transition-colors"
              onClick={() => setShowModal(false)}
            >
              <div
                className="w-full max-w-[640px] mx-auto bg-black text-white rounded-t-xl p-6 slide-up-modal relative"
                style={{ animation: 'slideUp 0.33s ease' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-6 text-gray-300 hover:text-gray-100 text-2xl"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
                <p className="text-2xl font-[900] mb-4 text-white leading-tight">
                  WOULD YOU DO ME A FAVOR?
                </p>
                <p className="text-lg">
                  I'm Rob and along with being a huge fan of The Killers,
                  <b> I just made my first album.</b> Some people say it sounds
                  like The Killers.
                  <br />
                  <br />
                  Would you do me a favor and give it a listen? Because... you'd
                  literally make my day!
                </p>
                <div className="flex gap-4 mt-4">
                  <a href="https://open.spotify.com/track/5B6F6RaBY79SLmTIZvOzLs?si=6b4ebbce9d0b42a9">
                    <Button
                      className="bg-[#1db954] text-white"
                      onClick={() =>
                        posthog.capture('killers_quiz_spotify_clicked')
                      }
                    >
                      Spotify
                    </Button>
                  </a>

                  <a href="https://music.apple.com/us/album/los-angeles/1839280247?i=1839280325">
                    <Button
                      className="bg-[#F21C3F] text-white"
                      onClick={() =>
                        posthog.capture('killers_quiz_apple_music_clicked')
                      }
                    >
                      Apple Music
                    </Button>
                  </a>
                  <a href="https://www.youtube.com/watch?v=9zv06vcJkEE">
                    <Button
                      className="bg-[#FF0000] text-white"
                      onClick={() =>
                        posthog.capture('killers_quiz_youtube_clicked')
                      }
                    >
                      YouTube
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
          <style jsx>{`
            @keyframes slideUp {
              from {
                transform: translateY(100%);
                animation-delay: 1s;
              }
              to {
                transform: translateY(0%);
                animation-delay: 1s;
              }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-4 md:p-6"
      style={{
        backgroundImage: "url('/your-background-image.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="max-w-[720px] mx-auto py-[48px]">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-[900] mb-4 text-gray-900">
            THE KILLERS QUIZ
          </h1>
          <div className="flex justify-between items-center">
            <p className="text-xl md:text-2xl text-gray-900">
              Song {currentSongIndex + 1} of {songs.length}
            </p>
            <div className="text-xl md:text-2xl font-bold text-gray-900">
              Score: {score}
            </div>
          </div>
        </div>

        <div className="bg-white bg-opacity-60 p-6 md:p-8 border-4 border-gray-200 rounded-md">
          {!showAnswer ? (
            <>
              <div className="text-center mb-8">
                <button
                  onClick={playClip}
                  className="bg-[#128D7E] hover:bg-[#085950] text-white px-8 py-4 rounded-md text-xl md:text-2xl font-bold mb-4 transition w-full md:w-auto"
                >
                  ▶ Play {currentDuration}s Clip
                </button>
                <p className="text-md text-gray-700 font-bold mt-4">
                  Attempts left: {attemptsLeft} / 4
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Points:{' '}
                  {attemptsLeft === 4
                    ? 8
                    : attemptsLeft === 3
                      ? 4
                      : attemptsLeft === 2
                        ? 2
                        : 1}
                </p>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  value={currentGuess}
                  onChange={(e) => setCurrentGuess(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleGuess()}
                  placeholder="Enter song title..."
                  className="w-full p-4 rounded-md bg-white text-gray-900 border-2 border-gray-300 focus:border-[#128D7E] outline-none text-lg"
                />
              </div>

              <button
                onClick={handleGuess}
                disabled={!currentGuess.trim()}
                className="w-full bg-[#128D7E] hover:bg-[#085950] disabled:bg-gray-400 text-white px-8 py-4 rounded-md text-xl font-bold transition"
              >
                Submit Guess
              </button>

              {guesses.length > 0 && (
                <div className="mt-6 bg-gray-100 p-4 rounded-md">
                  <p className="text-sm font-bold text-gray-700 mb-2">
                    Previous guesses:
                  </p>
                  {guesses.map((guess, i) => (
                    <p key={i} className="text-gray-900">
                      ❌ {guess}
                    </p>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <h2 className="text-3xl md:text-4xl font-[900] mb-4 text-gray-900">
                {attemptsLeft > 0 ? '🎉 CORRECT!' : '❌ OUT OF ATTEMPTS!'}
              </h2>
              <p className="text-xl mb-2 text-gray-700">The answer was:</p>
              <p className="text-2xl md:text-3xl font-[900] text-[#128D7E]">
                {currentSong.title}
              </p>
              {attemptsLeft > 0 && (
                <p className="text-lg text-gray-700 mt-4">
                  +
                  {attemptsLeft === 4
                    ? 8
                    : attemptsLeft === 3
                      ? 4
                      : attemptsLeft === 2
                        ? 2
                        : 1}{' '}
                  points!
                </p>
              )}
            </div>
          )}
        </div>

        <audio ref={audioRef} src={currentSong.audioFile} />
      </div>
    </div>
  );
}
